import React, { ReactNode } from 'react';
import { Box, Checkbox, CircularProgress, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Toolbar, Tooltip, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LastPageIcon from '@mui/icons-material/LastPage';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import _ from 'lodash';
import { Group } from '../../models';
// scss
import './scss/table.component.scss'
import i18n from 'i18next';
import DeleteDialogComponent from '../dialog/delete-dialog.component';
import { KeyboardArrowDown } from '@mui/icons-material';
import ButtonComponent, { ButtonComponentType } from '../button/button.component';

// table组件
export interface Data {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

// 头部类型声明
export interface HeadCell {
    id: string;
    label: string;
    // -1 不显示排序得框框 ， 0 不排序  1 降序 2 升序
    sortStatus?: number;
    renderFunction?: (row: Data, index: number) => ReactNode;
    width?: string;
    /** 列最小宽度，避免表头/内容过挤，配合横向滚动使用 */
    minWidth?: string;
    /** 横向滚动时固定在该侧（依赖 TableContainer 横向 overflow；左侧列宜连续配置） */
    sticky?: 'left' | 'right';
    /** 累加 left/right 偏移时使用的列宽（px）；不传则从 width/minWidth 解析 px，否则默认 120 */
    stickyWidthPx?: number;
}

/** 前置固定列默认宽度（px），可与 stickyLeadingWidthsPx 覆盖 */
const DEFAULT_STICKY_LEADING_WIDTH_PX = { checkbox: 48, serial: 72, expand: 56 } as const;

/** 未传 originSelect 时的稳定空引用；避免默认 `[]` 每渲染新建引用导致「同步初始值」useEffect 死循环 */
const DEFAULT_ORIGIN_SELECT: Data[] = [];


function getHeadCellWidthPx(cell: HeadCell): number {
    if (cell.stickyWidthPx != null && cell.stickyWidthPx > 0) {
        return cell.stickyWidthPx;
    }
    const parsePx = (v?: string): number => {
        if (!v) {
            return 0;
        }
        const m = String(v).trim().match(/^(\d+(?:\.\d+)?)px$/i);
        return m ? Number(m[1]) : 0;
    };
    const w = parsePx(cell.width) || parsePx(cell.minWidth);
    return w > 0 ? w : 120;
}

type StickyLeadingStyles = {
    checkbox?: React.CSSProperties;
    serial?: React.CSSProperties;
    expand?: React.CSSProperties;
};

/** 固定列与滚动区之间的侧向分隔，仅用阴影、不用左右 border */
function leftStickyEdgeStyle(): Pick<React.CSSProperties, 'boxShadow'> {
    return {
        boxShadow: '4px 0 10px -4px rgba(0,0,0,0.12)',
    };
}

function rightStickyEdgeStyle(): Pick<React.CSSProperties, 'boxShadow'> {
    return {
        boxShadow: '-4px 0 10px -4px rgba(0,0,0,0.12)',
    };
}

function buildTableStickyColumnStyles(params: {
    headCellList: HeadCell[];
    /** 固定左侧复选框、展开列（不含序号，序号见 stickySerialColumn） */
    stickyLeadingLeft: boolean;
    /** 序号列单独固定，可与 stickyLeadingLeft 组合；仅开启此项时只固定序号 */
    stickySerialColumn: boolean;
    showChecked: number;
    hasSerialColumn: boolean;
    hasExpand: boolean;
    stickyLeadingWidthsPx?: Partial<{ checkbox: number; serial: number; expand: number }>;
    stickyHeader: boolean;
    headerBg: string;
    bodyBg: string;
}): {
    leadingHead: StickyLeadingStyles;
    leadingBody: StickyLeadingStyles;
    headDataStyles: Array<React.CSSProperties | undefined>;
    bodyDataStyles: Array<React.CSSProperties | undefined>;
} {
    const w = { ...DEFAULT_STICKY_LEADING_WIDTH_PX, ...params.stickyLeadingWidthsPx };
    const zHead = params.stickyHeader ? 4 : 3;
    const zBody = 2;
    const leadingHead: StickyLeadingStyles = {};
    const leadingBody: StickyLeadingStyles = {};
    let lead = 0;

    const checkboxSticky = params.stickyLeadingLeft && params.showChecked !== 0;
    const serialSticky =
        params.hasSerialColumn && (params.stickyLeadingLeft || params.stickySerialColumn);
    const expandSticky = params.stickyLeadingLeft && params.hasExpand;

    if (checkboxSticky) {
        leadingHead.checkbox = {
            position: 'sticky',
            left: lead,
            zIndex: zHead,
            backgroundColor: params.headerBg,
            ...leftStickyEdgeStyle(),
        };
        leadingBody.checkbox = {
            position: 'sticky',
            left: lead,
            zIndex: zBody,
            backgroundColor: params.bodyBg,
            ...leftStickyEdgeStyle(),
        };
        lead += w.checkbox;
    }
    if (serialSticky) {
        leadingHead.serial = {
            position: 'sticky',
            left: lead,
            zIndex: zHead,
            backgroundColor: params.headerBg,
            ...leftStickyEdgeStyle(),
        };
        leadingBody.serial = {
            position: 'sticky',
            left: lead,
            zIndex: zBody,
            backgroundColor: params.bodyBg,
            ...leftStickyEdgeStyle(),
        };
        lead += w.serial;
    }
    if (expandSticky) {
        leadingHead.expand = {
            position: 'sticky',
            left: lead,
            zIndex: zHead,
            backgroundColor: params.headerBg,
            ...leftStickyEdgeStyle(),
        };
        leadingBody.expand = {
            position: 'sticky',
            left: lead,
            zIndex: zBody,
            backgroundColor: params.bodyBg,
            ...leftStickyEdgeStyle(),
        };
        lead += w.expand;
    }

    const dataBaseLeft = lead;
    const widths = params.headCellList.map(getHeadCellWidthPx);
    const headDataStyles: Array<React.CSSProperties | undefined> = params.headCellList.map(() => undefined);
    const bodyDataStyles: Array<React.CSSProperties | undefined> = params.headCellList.map(() => undefined);

    let cursorLeft = dataBaseLeft;
    params.headCellList.forEach((cell, i) => {
        if (cell.sticky !== 'left') {
            return;
        }
        headDataStyles[i] = {
            position: 'sticky',
            left: cursorLeft,
            zIndex: zHead,
            backgroundColor: params.headerBg,
            ...leftStickyEdgeStyle(),
        };
        bodyDataStyles[i] = {
            position: 'sticky',
            left: cursorLeft,
            zIndex: zBody,
            backgroundColor: params.bodyBg,
            ...leftStickyEdgeStyle(),
        };
        cursorLeft += widths[i];
    });

    let cursorRight = 0;
    for (let i = params.headCellList.length - 1; i >= 0; i--) {
        const cell = params.headCellList[i];
        if (cell.sticky !== 'right') {
            continue;
        }
        const headRight: React.CSSProperties = {
            position: 'sticky',
            right: cursorRight,
            zIndex: zHead,
            backgroundColor: params.headerBg,
            ...rightStickyEdgeStyle(),
        };
        const bodyRight: React.CSSProperties = {
            position: 'sticky',
            right: cursorRight,
            zIndex: zBody,
            backgroundColor: params.bodyBg,
            ...rightStickyEdgeStyle(),
        };
        headDataStyles[i] = { ...headDataStyles[i], ...headRight };
        bodyDataStyles[i] = { ...bodyDataStyles[i], ...bodyRight };
        cursorRight += widths[i];
    }

    return { leadingHead, leadingBody, headDataStyles, bodyDataStyles };
}

interface EnhancedTableProps {
    stickyHeaderItemClass?: string;
    headCellList: Array<HeadCell>;
    numSelected: number;
    rowCount: number;
    showChecked: number;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSort?: (headCell: HeadCell) => void;
    // 单选或者多选，默认多选
    selectType?: 'RADIO' | 'CHECK';
    hasExpand?: boolean;
    // 是否显示表头序号
    isShowSerialNum?: boolean;
    /** 与表体一致：复选框、序号、展开、数据列的左侧固定样式 */
    leadingHeadStickyStyles?: StickyLeadingStyles;
    /** 与 headCellList 下标对齐的数据列表头固定样式 */
    headDataStickyStyles?: Array<React.CSSProperties | undefined>;
    /** 序号列布局宽度（与 sticky 累加用宽度一致，避免实际列宽与 left 偏移不符产生缝隙） */
    serialColumnLayoutStyle?: React.CSSProperties;
}

interface TableRowCellProps {
    headCellList: Array<HeadCell>;
    row: Data;
    index: number;
    hasExpand?: boolean;
    isExpandValue?: boolean;
    onHandleExpand?: (_id: string, index: number, isExpandValue?: boolean) => void;
    /** 展开列左侧固定样式（与 stickyLeadingLeft 配套） */
    expandStickyStyle?: React.CSSProperties;
    /** 与 headCellList 下标对齐的数据列单元格固定样式 */
    dataColumnStickyStyles?: Array<React.CSSProperties | undefined>;
}

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // paddingLeft: theme.spacing(2),
            // paddingRight: theme.spacing(1),
        },
        highlight:
            theme.palette.mode === 'light'
                ? {
                    color: '#fff',
                    background: 'linear-gradient(72.47deg, rgb(115, 103, 240) 22.16%, rgba(115, 103, 240, 0.7) 76.47%)',
                    borderTopLeftRadius: 7,
                    borderTopRightRadius: 7,

                }
                : {
                    color: theme.palette.text.primary,
                    backgroundColor: 'rgba(115, 103, 240, 0.16)'
                },
        title: {
            flex: '1 1 100%',
        }
    })
);

interface EnhancedTableToolbarProps {
    numSelected: number;
    handleDelete: () => void;
    renderToolBar?: () => React.ReactNode;
    // 自定义多选操作栏
    renderToolBarRight?: () => React.ReactNode;
}

// $ 复选框的头部
export const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    const classes = useToolbarStyles();
    const { numSelected, handleDelete, renderToolBar, renderToolBarRight } = props;
    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
            sx={{ padding: '0 16px' }}
        >
            {numSelected > 0 ? (
                <>
                    <Typography className={classes.title} color='inherit' variant='subtitle1' component='div'>
                        {numSelected} {i18n.t('table_component_text_selected')}
                    </Typography>
                    <Tooltip title={i18n.t('general_text_delete')}>
                        <IconButton aria-label='delete' onClick={() => handleDelete()}>
                            <DeleteIcon style={{ color: '#fff' }} />
                        </IconButton>
                    </Tooltip>
                    {renderToolBarRight?.()}
                </>

            ) : (
                <>
                    {renderToolBar?.()}
                </>
            )}
        </Toolbar>
    );
};

// $ 头部信息
export function EnhancedTableHead(props: EnhancedTableProps) {
    const { onSelectAllClick, numSelected, rowCount, showChecked, headCellList,
        handleSort, selectType, hasExpand, stickyHeaderItemClass = '', isShowSerialNum,
        leadingHeadStickyStyles, headDataStickyStyles, serialColumnLayoutStyle } = props;
    const classes = useStyles();
    return (
        <TableRow className={classes.tableHeadBg}>
            {/* 与表体列顺序一致：复选框 → 序号 → 展开 → 数据列 */}
            {
                showChecked !== 0 && (
                    <TableCell
                        padding='checkbox'
                        className={clsx(stickyHeaderItemClass, leadingHeadStickyStyles?.checkbox && 'table-sticky-cell')}
                        style={leadingHeadStickyStyles?.checkbox}
                    >
                        {selectType === 'RADIO' ? null :
                            <Checkbox
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{ 'aria-label': 'select all desserts' }}
                            />
                        }
                    </TableCell>
                )
            }
            {
                isShowSerialNum &&
                <TableCell
                    className={clsx(stickyHeaderItemClass, leadingHeadStickyStyles?.serial && 'table-sticky-cell')}
                    style={{ ...serialColumnLayoutStyle, ...leadingHeadStickyStyles?.serial }}
                >
                    <div className='headerTableCell'
                        style={{ cursor: 'default' }}
                    >
                        <p className='headerLabel'>{i18n.t('table_component_text_serial_number')}</p>
                    </div>
                </TableCell>
            }
            {
                hasExpand ?
                    <TableCell
                        className={clsx(stickyHeaderItemClass, leadingHeadStickyStyles?.expand && 'table-sticky-cell')}
                        style={{ width: '3%', ...leadingHeadStickyStyles?.expand }}
                    >
                    </TableCell>
                    : null
            }
            {/* 列表头部数据 */}
            {
                headCellList.map((headCell: HeadCell, colIndex: number) => (
                    <TableCell
                        key={headCell.id}
                        className={clsx(stickyHeaderItemClass, headDataStickyStyles?.[colIndex] && 'table-sticky-cell')}
                        width={headCell.width}
                        style={{ width: headCell.width, minWidth: headCell.minWidth, ...headDataStickyStyles?.[colIndex] }}
                    >
                        <div className='headerTableCell'
                            style={{ cursor: [0, 1, 2].includes(headCell.sortStatus ?? -1) ? 'pointer' : 'default' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                [0, 1, 2].includes(headCell.sortStatus ?? -1) && handleSort?.(headCell);
                            }}
                        >
                            <p className='headerLabel'>{headCell.label}</p>

                            {/* 需要排序字段才显示 */}
                            {
                                [0, 1, 2].includes(headCell.sortStatus ?? -1) ? (
                                    <div className='sortButton'>
                                        <ArrowDropDownIcon
                                            className='down'
                                            style={{ color: headCell.sortStatus === 2 ? '#1F78FB' : '#707174' }}
                                        />
                                        <ArrowDropDownIcon
                                            className='down'
                                            style={{ color: headCell.sortStatus === 1 ? '#1F78FB' : '#707174' }}
                                        />
                                    </div>
                                ) : null
                            }
                        </div>
                    </TableCell>
                ))
            }
        </TableRow >
    );
}

/**
 * 默认表内容
 */
export function RenderChildrenBase(props: TableRowCellProps) {

    const { row, index, headCellList, hasExpand, isExpandValue, expandStickyStyle, dataColumnStickyStyles } = props;

    return (
        <>
            {hasExpand && (
                <TableCell
                    size='small'
                    className={clsx(expandStickyStyle && 'table-sticky-cell')}
                    style={{ width: '3%', ...expandStickyStyle }}
                >
                    <ButtonComponent
                        variant='text'
                        sx={(theme) => { return { color: isExpandValue ? theme.palette.primary.main : theme.palette.secondary.main } }}
                        handleClick={() => {
                            props.onHandleExpand && props.onHandleExpand(row._id as string, index, isExpandValue);
                        }}>
                        {/* 操作对应接团id的变量控制 */}
                        {isExpandValue ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    </ButtonComponent>
                </TableCell>
            )}
            {headCellList?.map((column, columnKey) => {
                return (
                    <TableCell
                        key={columnKey}
                        size='small'
                        className={clsx(dataColumnStickyStyles?.[columnKey] && 'table-sticky-cell')}
                        width={column.width}
                        style={{ width: column.width, minWidth: column.minWidth, ...dataColumnStickyStyles?.[columnKey] }}
                    >
                        {column.renderFunction
                            ? column.renderFunction(row, props.index)
                            : _.get(row, column.id, '')}
                    </TableCell >
                );
            })}
        </>
    )
}

// 表格组件自定义样式
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // 表格无阴影，由外层 Card 统一处理投影
        tableShadow: {
            boxShadow: 'none'
        },
        tableHeadBg: {
            // 表头背景统一使用语义白色变量，深浅模式均适用
            '& .MuiTableCell-stickyHeader': {
                background: 'var(--fill-neutral-100)'
            }
        },
        // border-collapse: collapse 下 td 的 box-shadow 作行线常被浏览器忽略；改用 border-bottom 保证行间分隔可见
        tableRoot: {
            backgroundColor: theme.palette.background.paper,
            borderCollapse: 'collapse',
            '& .MuiTableHead-root .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
                borderBottom: `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
            },
            '& .MuiTableCell-stickyHeader': {
                boxShadow: 'none',
            },
        },
    })
);

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

// 自定义的页脚分页组件
function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label='first page'
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label='previous page'
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label='next page'
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label='last page'
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

// $ 表格
export default function TableComponent(
    props: TableComponentProps
): JSX.Element {

    const { renderExtraCollapseComponent, showChecked, list, headCellList, isPagination = false, allCount = 0, selectType,
        stickyHeader = true, currentPage = 0, rowsPerPageOptions = [20, 50, 100], rowsPerPage = 20, extraClass = '',
        isTableLoading = false, stickyHeaderItemClass = '', compareSymbol = '_id', originSelect = DEFAULT_ORIGIN_SELECT, synchronize = true, isUseFold,
        isUseCustomPagination, operateCollapseComponent, handleSort, handleCheck, handleDeleteAll,
        filterCheckData, renderToolBar, hasExpand, changePageCallback, isActivateSummation, summationArray, isCurrentPageData, onRowClick,
        selectedRowVariant = 'default', stickyLeadingLeft = false, stickySerialColumn = false, stickyLeadingWidthsPx } = props;

    // 自定义表格样式
    const classes = useStyles();
    const theme = useTheme();
    // 复选框选中的数据
    const [selected, setSelected] = React.useState<Data[]>(originSelect);
    // 当前页数  切换页数
    const [page, setPage] = React.useState<number>(currentPage);
    // 设置当前页数显示的行数
    const [curRowsPerPage, setCurRowsPerPage] = React.useState<number>(rowsPerPage)
    // 控制复选框批量删除提示
    const [deleteAllDialog, setDeleteAllDialog] = React.useState<boolean>(false);
    // 操作列表折叠组件
    const [expandValue, setExpandValue] = React.useState<typeof operateCollapseComponent>({
        _id: '',
        activeIndex: 0
    })
    // 表格数据
    const tableData = isCurrentPageData ? list : list.slice(page * curRowsPerPage, page * curRowsPerPage + curRowsPerPage);

    const hasSerialColumn = Boolean(isActivateSummation && list.length > 0);
    const hasDataStickyColumns = headCellList.some((h) => h.sticky === 'left' || h.sticky === 'right');
    const stickyColumnEnabled = stickyLeadingLeft || stickySerialColumn || hasDataStickyColumns;

    const mergedStickyLeadingWidths = React.useMemo(
        () => ({ ...DEFAULT_STICKY_LEADING_WIDTH_PX, ...stickyLeadingWidthsPx }),
        [stickyLeadingWidthsPx],
    );
    const serialColumnLayoutStyle: React.CSSProperties | undefined = hasSerialColumn
        ? {
            width: mergedStickyLeadingWidths.serial,
            minWidth: mergedStickyLeadingWidths.serial,
            maxWidth: mergedStickyLeadingWidths.serial,
            boxSizing: 'border-box',
        }
        : undefined;

    const stickyColumnStyles = React.useMemo(
        () =>
            buildTableStickyColumnStyles({
                headCellList,
                stickyLeadingLeft,
                stickySerialColumn,
                showChecked,
                hasSerialColumn,
                hasExpand: Boolean(hasExpand),
                stickyLeadingWidthsPx,
                stickyHeader,
                headerBg: 'var(--fill-neutral-100)',
                bodyBg: theme.palette.background.paper,
            }),
        [
            headCellList,
            stickyLeadingLeft,
            stickySerialColumn,
            showChecked,
            hasSerialColumn,
            hasExpand,
            stickyLeadingWidthsPx,
            stickyHeader,
            theme.palette.background.paper,
        ],
    );

    // $ 列表项删除需要同步复选数据, 可能在外层删除
    React.useEffect(() => {
        /**
         * $ 1.列表长度有改变，因此需要排除复选框的数据 (主要为了解决单删除没有同步到)
         * $ 2.可能是搜索导致的，如果是搜索就不应该重置selected
         */
        // 如果只是搜索不让他清除掉selected
        if (synchronize) {
            const originSelected = _.cloneDeep(selected);
            const newArr: Array<Data> = [];
            originSelected.forEach((item) => {
                // todo 这里需要优化
                const idList = list.map((i) => {
                    return i?.[compareSymbol];
                })
                if (idList.includes(item?.[compareSymbol])) {
                    newArr.push(item)
                }
            })
            setSelected(newArr);
        }
        // eslint-disable-next-line
    }, [allCount, page, curRowsPerPage]);

    React.useEffect(() => {
        setPage(currentPage)
    }, [currentPage])

    // 同步初始值
    React.useEffect(() => {
        setSelected(originSelect);
        // 每次刷新列表时设置当前页数为第一页
        if (isTableLoading && (!isCurrentPageData)) {
            setPage(0);
        }
        // eslint-disable-next-line
    }, [originSelect, isTableLoading])

    // 选中所有或者清除所有
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * 1.设置选中只选中当前页行数的数据
         * 2.全选按钮,选中取消,没有选中全选
         * 3.触发父类的 handleCheck方法
         * $ 4.过滤不需要被勾选在的数据
         */
        if (event.target.checked) {
            // 设置选中只选中当前页行数的数据
            // 当前页行数选中的第一个索引
            const firstIndex = page * curRowsPerPage;
            // 当前页行数选中的最后一个索引
            const lastIndex = (page + 1) * curRowsPerPage;
            const currentRowsSelected = list.slice(firstIndex, lastIndex);
            // $ 数据过滤
            const newSelected = filterCheckData?.(currentRowsSelected) || currentRowsSelected;
            handleCheck?.(newSelected);
            setSelected(newSelected);
            return;
        }
        handleCheck?.([]);
        setSelected([]);
    };

    // 空值函数 没有获取到数据时
    const emptyFunc = () => { }

    // 单机选中单行
    const handleClick = (event: React.MouseEvent<unknown>, row: Data) => {
        /**
         * 1.这里单选状态和复选状态
         * 2.保存选中的数据
         * 3.过滤掉不需要的字段勾选的
         */
        // 不显示复选框时，若有行点击回调则执行（如点击行打开详情）
        if (showChecked === 0) {
            onRowClick?.(row);
            return;
        }
        // 选中的数据处理
        // const selectedIndex = selected.findIndex((item) => {
        //     if (item[compareSymbol]) {
        //         return item[compareSymbol] === row[compareSymbol];
        //     }
        //     return false;
        // });

        // 选中的数据处理
        const selectedItem = selected.some((item) => {
            if (item[compareSymbol]) {
                return item[compareSymbol] === row[compareSymbol];
            }
            return false;
        });

        let newSelected: Data[] = [];
        // if (selectType === 'RADIO') {
        //     if (selectedIndex === -1) {
        //         // $ 如果是单选而且选择是禁用数据，不进行替换
        //         if (filterCheckData?.([row])?.length === 0) return
        //         newSelected = [row];
        //     } else if (selectedIndex === 0) {

        //         newSelected = [];
        //     }
        // } else {
        //     // splice会影响原数组
        //     if (selectedIndex === -1) {
        //         newSelected = newSelected.concat(selected, row);
        //     } else if (selectedIndex === 0) {
        //         newSelected = newSelected.concat(selected.slice(1));
        //     } else if (selectedIndex === selected.length - 1) {
        //         newSelected = newSelected.concat(selected.slice(0, -1));
        //     } else if (selectedIndex > 0) {
        //         newSelected = newSelected.concat(
        //             selected.slice(0, selectedIndex),
        //             selected.slice(selectedIndex + 1)
        //         );
        //     }
        // }
        if (selectType === 'RADIO') {
            if (!selectedItem) {
                if (filterCheckData?.([row])?.length === 0) return;
                newSelected = [row];
            } else {
                newSelected = [];
            }
        } else {
            if (!selectedItem) {
                newSelected = [...selected, row];
            } else {
                newSelected = selected.filter((item) => {
                    return !(item[compareSymbol] === row[compareSymbol]);
                });
            }
        }
        // $ 数据过滤
        // newSelected = filterCheckData?.(newSelected) || newSelected;
        const finalSelected = filterCheckData?.(newSelected.filter((item) => !!item)) || newSelected;
        handleCheck?.(finalSelected || []);
        setSelected(finalSelected || newSelected);
    };

    // 改变当前页数  下一页 | 上一页
    const handleSetPage = (event: any, newPage: number) => {
        setPage(newPage);
        changePageCallback?.({ page: newPage, curRowsPerPage });
        /**
         * 讨论决定 切换表格页数时重置复选框选择的数据
         */
        if (selected.length > 0) {
            setSelected([]);
        }
    }

    // 设置每页显示的...行数
    const handleSetRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rowsPerPage = parseInt(event.target.value, 10)
        setCurRowsPerPage(rowsPerPage);
        setPage(0);
        changePageCallback?.({ page: 0, curRowsPerPage: rowsPerPage });
        /**
         * 讨论决定 改变表格每页显示的行数时重置复选框选择的数据
         */
        if (selected.length > 0) {
            setSelected([]);
        }
    };

    // 判断是否被选中了 当前行
    const isSelected = (row: Data) => selected.findIndex((item) => {
        if (item[compareSymbol]) {
            return item[compareSymbol] === row[compareSymbol];
        }
        return false;
    }) !== -1;

    // 关闭或者开启 批量删除弹窗
    function toggleDialog(status: boolean) {
        setDeleteAllDialog(status);
    }

    // 批量删除确定按钮
    async function handleConfirmDelete(status: number) {
        /**
         * 1.触发父级删除事件
         * 2.清空选中的复选内容
         * 3.关闭提示框
         */
        if (status === 1) {
            await handleDeleteAll?.(selected);
            setSelected([]);
            setDeleteAllDialog(false);
        }
    }

    // 获取到没有数据时 colSpan的值
    function getColSpan() {
        // 头部加复选框
        const num = (headCellList?.length ?? 0) + (showChecked === 0 ? 0 : 1) + (hasExpand ? 1 : 0);
        return num
    }

    //
    /**
     * 操作折叠组件展开
     * @param _id 表格_id
     * @param activeKey 表格索引
     * @param isExpand 展开的值
     */
    function handleExpand(_id: string, activeKey: number, isExpand?: boolean) {
        // 存在展开的项时关闭
        if (isExpand) { setExpandValue({ _id: '', activeIndex: -1 }) }
        // 不存在则展开
        else { setExpandValue({ _id, activeIndex: activeKey }) }
    }

    // 复选框是否可用
    function checkDisabled(row: any) {
        /**
         * 1.过滤函数不存在返回false
         * 2.过滤函数存在数组，说没没用不符合数据 返回false
         */
        return filterCheckData ? filterCheckData?.([row])?.length ? false : true : false
    }

    const tableRootStyle: React.CSSProperties = {
        width: '100%',
    };

    return (
        <div
            className={`tableComponent ${classes.tableShadow} ${extraClass} ${selectedRowVariant === 'primary' ? 'selected-row-primary' : ''}`}
            style={tableRootStyle}
        >
            {
                //  $ 复选框删除提示框 (只有1的时候才显示)
                [1].includes(showChecked) && selectType !== 'RADIO' && (
                    <EnhancedTableToolbar
                        {...{
                            numSelected: selected.length,
                            handleDelete: () => { toggleDialog(true) },
                            renderToolBar,
                            renderToolBarRight: () => props.renderToolBarRight?.(selected),
                        }}
                    />
                )
            }
            {/* 主要内容 */}
            <TableContainer className='tableContainer'>
                <Table
                    stickyHeader={stickyHeader}
                    aria-labelledby='tableTitle'
                    aria-label='enhanced table'
                    className={`table ${classes.tableRoot}`}
                >
                    {/* table头部 */}
                    <TableHead>
                        <EnhancedTableHead
                            stickyHeaderItemClass={stickyHeaderItemClass}
                            headCellList={headCellList}
                            showChecked={showChecked}
                            numSelected={selected.length}
                            onSelectAllClick={handleSelectAllClick}
                            rowCount={selected.length}
                            handleSort={handleSort}
                            selectType={selectType}
                            hasExpand={hasExpand}
                            isShowSerialNum={isActivateSummation && list.length > 0}
                            leadingHeadStickyStyles={stickyColumnEnabled ? stickyColumnStyles.leadingHead : undefined}
                            headDataStickyStyles={stickyColumnEnabled ? stickyColumnStyles.headDataStyles : undefined}
                            serialColumnLayoutStyle={serialColumnLayoutStyle}
                        />
                    </TableHead>
                    {/* loading 加载框显示 */}
                    {
                        isTableLoading && (
                            <TableBody className='loadingBody'>
                                <TableRow className='tableRow'>
                                    <TableCell colSpan={getColSpan()} className='tableCell' >
                                        <CircularProgress sx={(theme) => { return { color: theme.palette.primary.main } }} />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    }

                    {/* table内容 */}
                    <TableBody className='tableBody'>
                        {
                            list.length > 0 ? (
                                // 截取当前页行数数据
                                tableData.map((row, index) => {
                                    const isItemSelected = isSelected(row);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    // 是否展开折叠组件列表
                                    const isExpandValue = expandValue?.activeIndex === index && expandValue?._id === (row as Group)?._id;
                                    return (
                                        <React.Fragment key={row?.[compareSymbol] || index}>
                                            <TableRow
                                                hover
                                                onClick={(event) => isUseFold ? emptyFunc() : handleClick(event, row)}
                                                role='checkbox'
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row?.[compareSymbol] || index}
                                                selected={isItemSelected}
                                                className='borderBottom-set'

                                            >
                                                {/* 复选框 */}
                                                {
                                                    showChecked !== 0 && (
                                                        <TableCell
                                                            size='small'
                                                            padding='checkbox'
                                                            className={clsx(stickyColumnEnabled && stickyColumnStyles.leadingBody.checkbox && 'table-sticky-cell')}
                                                            style={stickyColumnEnabled ? stickyColumnStyles.leadingBody.checkbox : undefined}
                                                            onClick={(event) => isUseFold ? handleClick(event, row) : emptyFunc()}
                                                        >
                                                            <Checkbox
                                                                disabled={checkDisabled(row)}
                                                                checked={isItemSelected}
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </TableCell>
                                                    )
                                                }
                                                {
                                                    (isActivateSummation && list.length > 0) ?
                                                        <TableCell
                                                            size='small'
                                                            className={clsx(stickyColumnEnabled && stickyColumnStyles.leadingBody.serial && 'table-sticky-cell')}
                                                            style={{
                                                                borderTop: 0,
                                                                ...serialColumnLayoutStyle,
                                                                ...(stickyColumnEnabled ? stickyColumnStyles.leadingBody.serial : {}),
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </TableCell>
                                                        : null
                                                }

                                                {/* 子元素 */}
                                                {
                                                    <RenderChildrenBase
                                                        {...{
                                                            headCellList,
                                                            row,
                                                            index,
                                                            hasExpand,
                                                            isExpandValue,
                                                            onHandleExpand: handleExpand,
                                                            expandStickyStyle: stickyColumnEnabled ? stickyColumnStyles.leadingBody.expand : undefined,
                                                            dataColumnStickyStyles: stickyColumnEnabled ? stickyColumnStyles.bodyDataStyles : undefined,
                                                        }}
                                                    />
                                                }
                                            </TableRow>
                                            {/* 折叠的组件 用于集团列表页展示授权点和授权集合相关信息 */}
                                            {
                                                renderExtraCollapseComponent &&
                                                <TableRow style={{}}>
                                                    <TableCell
                                                        style={{
                                                            padding: '0px 10px',
                                                            borderTop: 0,
                                                        }}
                                                        colSpan={10}
                                                    >
                                                        <Collapse
                                                            in={isExpandValue}
                                                            timeout={450}
                                                            unmountOnExit
                                                            className='collapse-class'
                                                        >
                                                            {renderExtraCollapseComponent(row, index)}
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                // 没有数据的时候信息提示
                                <TableRow
                                    hover
                                    role='checkbox'
                                    tabIndex={-1}
                                    className='borderBottom-set'
                                >

                                    <TableCell
                                        colSpan={getColSpan()}
                                        style={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontStyle: 'normal',
                                            fontSize: '14px',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        {i18n.t('table_component_text_no_or_more_data')}
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        {
                            (isActivateSummation && list.length > 0) ?
                                <TableRow
                                    hover
                                    role='checkbox'
                                    tabIndex={-1}
                                    className='borderBottom-set'
                                >
                                    {showChecked !== 0 && (
                                        <TableCell
                                            padding='checkbox'
                                            size='small'
                                            className={clsx(stickyColumnEnabled && stickyColumnStyles.leadingBody.checkbox && 'table-sticky-cell')}
                                            style={{
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                ...(stickyColumnEnabled ? stickyColumnStyles.leadingBody.checkbox : {}),
                                            }}
                                        />
                                    )}
                                    <TableCell
                                        size='small'
                                        className={clsx(stickyColumnEnabled && stickyColumnStyles.leadingBody.serial && 'table-sticky-cell')}
                                        style={{
                                            fontWeight: 'bold',
                                            fontStyle: 'normal',
                                            fontSize: '14px',
                                            borderTop: 0,
                                            ...serialColumnLayoutStyle,
                                            ...(stickyColumnEnabled ? stickyColumnStyles.leadingBody.serial : {}),
                                        }}
                                    >
                                        {`${i18n.t('table_component_text_total')}   `}
                                    </TableCell>
                                    {hasExpand && (
                                        <TableCell
                                            size='small'
                                            className={clsx(stickyColumnEnabled && stickyColumnStyles.leadingBody.expand && 'table-sticky-cell')}
                                            style={{
                                                width: '3%',
                                                fontWeight: 'bold',
                                                fontSize: '14px',
                                                ...(stickyColumnEnabled ? stickyColumnStyles.leadingBody.expand : {}),
                                            }}
                                        />
                                    )}
                                    {headCellList.map((headCell, sumIndex) => {
                                        const sumData = summationArray?.[sumIndex];
                                        return (
                                            <TableCell
                                                key={headCell.id}
                                                size='small'
                                                width={headCell.width}
                                                className={clsx(stickyColumnEnabled && stickyColumnStyles.bodyDataStyles[sumIndex] && 'table-sticky-cell')}
                                                style={{
                                                    width: headCell.width,
                                                    minWidth: headCell.minWidth,
                                                    fontWeight: 'bold',
                                                    fontStyle: 'normal',
                                                    fontSize: '14px',
                                                    ...(stickyColumnEnabled ? stickyColumnStyles.bodyDataStyles[sumIndex] : {}),
                                                }}
                                            >
                                                {sumData ?? ''}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                                : null
                        }

                    </TableBody>
                </Table>
            </TableContainer>
            {/* 分页 */}
            {
                isPagination && list.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        labelRowsPerPage={i18n.t('general_row_each_page')}
                        component='div'
                        count={allCount}
                        rowsPerPage={curRowsPerPage}
                        page={page}
                        onPageChange={(e, page) => handleSetPage(e, page)}
                        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSetRowsPerPage(event)}
                        // 自定义的页脚分页组件 isUseCustomPagination变量控制是否启用
                        ActionsComponent={isUseCustomPagination ? TablePaginationActions : undefined}
                    />
                )
            }
            {/* 是否批量删除所选择项目 */}
            <DeleteDialogComponent
                {...{
                    title: i18n.t('general_text_tips'),
                    open: deleteAllDialog,
                    onCancel: () => setDeleteAllDialog(false),
                    onConfirm: () => handleConfirmDelete(1),
                    onClose: () => setDeleteAllDialog(false),
                }}
            />
        </div >
    );
}

/**
 * 列表每行的功能按钮组
 */
export function TableRowFeatures(props: { buttons: ButtonComponentType[] }) {
    return (
        <div className='table-row-features'>
            {props?.buttons?.map((item, index) => {
                return (
                    <ButtonComponent
                        key={index}
                        variant={'contained'}
                        color={'primary'}
                        buttonStyle={{ margin: '0 0.5rem' }}
                        {...item}
                    >
                        {item.children}
                    </ButtonComponent>
                )
            })}
        </div>
    )
}


export type TableComponentProps = {
    // 列表项
    headCellList: Array<HeadCell>;
    // 列表数据
    list: Array<Data>;
    // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
    showChecked: number;
    // 是否分页
    isPagination?: boolean;
    // 总条数
    allCount?: number;
    // $ 当前页  description: 暂时改用组件内部的变量
    currentPage?: number;
    // $ 一页显示的条数 description: 暂时改用组件内部的变量
    rowsPerPage?: number;
    // 分页列表  一页 20 50 100 自定义
    rowsPerPageOptions?: Array<number>;
    // 额外的class
    extraClass?: string;
    // $ 换页  description: 暂时改用组件内部的函数
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleChangePage?: (event: any, page: number) => void;
    // $ 换一页显示的条数  description: 暂时改用组件内部的函数
    handleChangeRowsPerPage?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // 排序方法
    handleSort?: (headCell: HeadCell) => void;
    // 复选按钮触发事件
    handleCheck?: (selected: Data[]) => void;
    // 删除复选框选中项
    handleDeleteAll?: (selected: Data[]) => void | Promise<void>;
    // 根据已选数据生成批量删除弹窗说明
    getDeleteDialogDesc?: (selected: Data[]) => string;
    // 判断已选数据是否允许执行批量删除
    canDeleteSelected?: (selected: Data[]) => boolean;
    // 是否显示加载框 0不开启
    isTableLoading?: boolean;
    // 是否为粘性
    stickyHeader?: boolean;
    stickyHeaderItemClass?: string;
    // 单选或者多选，默认多选
    selectType?: 'RADIO' | 'CHECK';
    // $ 过滤复选框选中的数据
    filterCheckData?: (selected: Data[]) => Data[];
    // 渲染表头工具栏
    renderToolBar?: () => React.ReactNode;
    // 自定义多选操作栏
    renderToolBarRight?: (selected: Data[]) => React.ReactNode;
    // 作比较唯一表示  id \  _id \ item_id (后端唯一属性值，key不一致，因此可用传递唯一标识key ， 默认为_id)
    compareSymbol?: string;
    // 初始化复选框勾选数据
    originSelect?: Data[];
    // list长度改变是否同步selected  true同步 (删除，跟换页记录selected状态)
    synchronize?: boolean;
    // 是否用于行可以展示折叠的表格
    isUseFold?: boolean;
    // 操作集团下的对应的折叠组件  集团id区分
    /**
     * 操作集团下的对应的折叠组件
     * @param _id 操作的对应集团id
     * @param activeIndex 当前点击的索引
     */
    operateCollapseComponent?: {
        _id: string;
        activeIndex: number;
    };
    // 渲染额外的折叠组件
    renderExtraCollapseComponent?: (row: Data, index: number) => React.ReactNode;
    // 是否启用自定页脚分页组件  针对数据量大的表格  启用可以跳转表格数据的末尾
    isUseCustomPagination?: boolean;
    // 是否有折叠组件
    hasExpand?: boolean;
    // $ 操作切换页码、每页数量后返回当前页码、每页数量
    changePageCallback?: (params: { page: number; curRowsPerPage: number }) => void;
    // 启用表格计算合计功能
    isActivateSummation?: boolean;
    // 合计功能传入数据
    summationArray?: (number | string)[];
    // 后端分页 还是前端分页
    isCurrentPageData?: boolean;
    /** 行点击回调（在不显示复选框时生效，如点击行打开详情弹窗） */
    onRowClick?: (row: Data) => void;
    /** 选中行样式变体：默认浅色高亮，primary 为主题紫底高亮 */
    selectedRowVariant?: 'default' | 'primary';
    /** 横向滚动时将复选框、展开列固定在左侧；为 true 时若存在序号列则序号一并固定（也可用 stickySerialColumn 单独固定序号） */
    stickyLeadingLeft?: boolean;
    /** 横向滚动时单独固定序号列（需 isActivateSummation 且列表非空才有序号列）；可与 stickyLeadingLeft 叠加 */
    stickySerialColumn?: boolean;
    /** 前置固定列宽度（px），用于累加 left 偏移 */
    stickyLeadingWidthsPx?: Partial<{ checkbox: number; serial: number; expand: number }>;
}